/**
 * Amenities types for API communication
 * Using snake_case to match database conventions
 */

export type AmenityCategory =
  | 'shopping'
  | 'grocery'
  | 'culture'
  | 'dining'
  | 'education'
  | 'wellness'
  | 'healthcare'
  | 'transport'
  | 'services'

export interface AmenitySubCategory {
  id: string
  name: string
  parent_category: AmenityCategory
  icon?: string
  enabled: boolean
}

export interface AmenityPOI {
  id: string
  name: string
  category: AmenityCategory
  sub_category: string
  type: string
  distance: number
  walking_time?: number
  driving_time?: number
  coordinates: {
    latitude: number
    longitude: number
  }
  address?: string
  rating?: number
  open_now?: boolean
  photo_url?: string
}

export interface AmenityScore {
  value: number
  max_value: number
  category: 'low' | 'medium' | 'high' | 'excellent'
  label: string
  description?: string
}

export interface CategoryScore {
  category: AmenityCategory
  score: AmenityScore
  count: number
  sub_categories: AmenitySubCategory[]
  items: AmenityPOI[]
  expanded?: boolean
}

export interface AmenitiesData {
  overall_score: AmenityScore
  category_scores: CategoryScore[]
  total_pois: number
  search_radius: number
  walking_radius: number
  center_point: {
    latitude: number
    longitude: number
  }
  last_updated?: Date
}

export interface AmenitiesFilters {
  search_radius: number
  selected_categories: AmenityCategory[]
  show_only_open: boolean
  min_rating?: number
  max_distance?: number
  sort_by: 'distance' | 'rating' | 'category'
}

export interface AmenitiesContextState {
  data: AmenitiesData | null
  loading: boolean
  error: string | null
  filters: AmenitiesFilters
  selected_POI: AmenityPOI | null
  map_view: 'standard' | 'heatmap' | 'clusters'
  expanded_categories: Set<AmenityCategory>
}

export interface AmenitiesSearchParams {
  latitude: number
  longitude: number
  radius: number
  categories?: AmenityCategory[]
  limit?: number
}

// API Response from backend (duplicate of geography/poi.types.ts - remove this one)
// // PoiResponse is now imported from '../geography/poi.types'
// This interface was a duplicate and has been removed to resolve export conflicts

// Frontend processed response
export interface AmenitiesSearchResponse {
  pois: AmenityPOI[]
  total: number
  radius: number
  center: {
    latitude: number
    longitude: number
  }
}

// Category configurations
export const AMENITY_CATEGORIES: Record<
  AmenityCategory,
  {
    label: string
    icon: string
    color: string
    weight: number
  }
> = {
  shopping: {
    label: 'Shopping',
    icon: 'bag',
    color: '#ff2d55',
    weight: 0.7,
  },
  grocery: {
    label: 'Grocery',
    icon: 'shopping-cart',
    color: '#00b341',
    weight: 1.3,
  },
  culture: {
    label: 'Culture and Entertainment',
    icon: 'theater',
    color: '#af52de',
    weight: 0.8,
  },
  dining: {
    label: 'Dining and Drinking',
    icon: 'utensils',
    color: '#ff3b30',
    weight: 0.8,
  },
  education: {
    label: 'Education',
    icon: 'graduation-cap',
    color: '#ff9500',
    weight: 1.2,
  },
  wellness: {
    label: 'Wellness',
    icon: 'heart',
    color: '#ff69b4',
    weight: 1.1,
  },
  healthcare: {
    label: 'Healthcare',
    icon: 'hospital',
    color: '#7b61ff',
    weight: 1.4,
  },
  transport: {
    label: 'Transport',
    icon: 'bus',
    color: '#00ade6',
    weight: 1.5,
  },
  services: {
    label: 'Basic Amenities',
    icon: 'wrench',
    color: '#8e8e93',
    weight: 0.6,
  },
}

// Distance thresholds for scoring
export const DISTANCE_THRESHOLDS = {
  excellent: 250, // < 250m
  high: 500, // < 500m
  medium: 1000, // < 1km
  low: 2000, // < 2km
}

// Walking speed calculation (average 5 km/h)
export const WALKING_SPEED_MS = 1.39 // meters per second
export const DRIVING_SPEED_MS = 11.11 // meters per second (40 km/h in city)
